export function isWebAuthnSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.PublicKeyCredential !== "undefined" &&
    typeof navigator !== "undefined" &&
    typeof navigator.credentials !== "undefined"
  );
}

export async function isConditionalUISupported(): Promise<boolean> {
  if (!isWebAuthnSupported()) return false;
  try {
    return (
      (await PublicKeyCredential.isConditionalMediationAvailable?.()) ?? false
    );
  } catch {
    return false;
  }
}

export function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToArrayBuffer(base64: string): ArrayBuffer {
  if (typeof base64 !== "string" || !base64) {
    throw new Error("Invalid base64: expected non-empty string");
  }
  const cleaned = base64
    .replace(/\s/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const pad = cleaned.length % 4;
  const padded = pad > 0 ? cleaned + "=".repeat(4 - pad) : cleaned;
  try {
    const binary = atob(padded);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer as ArrayBuffer;
  } catch (e) {
    throw new Error(
      "Invalid base64: string is not correctly encoded. Check challenge/credentialId from server."
    );
  }
}

export function arrayBufferToBase64url(buffer: ArrayBuffer): string {
  const base64 = arrayBufferToBase64(buffer);
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function isUUID(str: string): boolean {
  return /^[0-9a-f]{8}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{4}-?[0-9a-f]{12}$/i.test(
    str
  );
}

function uuidToArrayBuffer(uuid: string): ArrayBuffer {
  const hex = uuid.replace(/-/g, "");
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return bytes.buffer as ArrayBuffer;
}

export function base64urlToArrayBuffer(input: string): ArrayBuffer {
  if (!input || typeof input !== "string") {
    throw new Error("Invalid input: expected non-empty string");
  }
  // If it looks like a UUID, convert hex to bytes
  if (isUUID(input)) {
    return uuidToArrayBuffer(input);
  }
  // Otherwise treat as base64/base64url
  return base64ToArrayBuffer(input);
}

export function stringToArrayBuffer(str: string): ArrayBuffer {
  return new TextEncoder().encode(str).buffer as ArrayBuffer;
}

export function uint8ArrayToString(arr: Uint8Array): string {
  return String.fromCharCode(...arr);
}

const DB_NAME = "xpay_webauthn";
const DB_VERSION = 1;
const STORE_NAME = "passkey_data";
const PASSKEY_EMAIL_KEY = "passkey_email";
const PASSKEY_MOBILE_NUMBER_KEY = "passkey_mobile_number";

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
  });
}

/**
 * Get the stored passkey email from IndexedDB
 */
export async function getPasskeyEmail(): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(PASSKEY_EMAIL_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

export async function setPasskeyEmail(email: string): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(email, PASSKEY_EMAIL_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch {
    // Silently fail - passkey will still work, just won't remember email
  }
}

/**
 * Remove the passkey email from IndexedDB
 */
export async function clearPasskeyEmail(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(PASSKEY_EMAIL_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch {
    // Silently fail
  }
}

/**
 * Get the stored passkey mobile number from IndexedDB
 */
export async function getPasskeyMobileNumber(): Promise<string | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(PASSKEY_MOBILE_NUMBER_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

export async function setPasskeyMobileNumber(
  mobileNumber: string
): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(mobileNumber, PASSKEY_MOBILE_NUMBER_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch {
    // Silently fail - passkey will still work, just won't remember mobile number
  }
}

/**
 * Remove the passkey mobile number from IndexedDB
 */
export async function clearPasskeyMobileNumber(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.delete(PASSKEY_MOBILE_NUMBER_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch {
    // Silently fail
  }
}

// ============================================================
// DPoP (Demonstration of Proof-of-Possession) using ECDSA P-256
// Per RFC 9449 - https://datatracker.ietf.org/doc/html/rfc9449
// ============================================================

const DPOP_PRIVATE_KEY_STORAGE_KEY = "dpop_private_key";
const DPOP_PUBLIC_KEY_JWK_STORAGE_KEY = "dpop_public_key_jwk";

/**
 * Minimal JWK for EC P-256 public key (only required fields per RFC 7517)
 */
export interface MinimalECPublicKeyJWK {
  kty: "EC";
  crv: "P-256";
  x: string;
  y: string;
}

/**
 * Create a minimal JWK from a full JWK (removes ext, key_ops, etc.)
 */
export function toMinimalJWK(jwk: JsonWebKey): MinimalECPublicKeyJWK {
  return {
    kty: "EC",
    crv: "P-256",
    x: jwk.x!,
    y: jwk.y!,
  };
}

/**
 * Calculate JWK Thumbprint (SHA-256) per RFC 7638
 * Uses canonical JSON with lexicographically ordered required members
 */
export async function calculateThumbprint(
  jwk: MinimalECPublicKeyJWK
): Promise<string> {
  // Canonical JSON with required members in lexicographic order for EC keys
  const canonical = JSON.stringify({
    crv: jwk.crv,
    kty: jwk.kty,
    x: jwk.x,
    y: jwk.y,
  });

  const hash = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonical)
  );

  return arrayBufferToBase64url(hash);
}

/**
 * Store DPoP Private Key in IndexedDB
 */
async function storeDPoPPrivateKey(key: CryptoKey): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(key, DPOP_PRIVATE_KEY_STORAGE_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch (err) {
    console.error("Failed to store DPoP private key:", err);
  }
}

/**
 * Store DPoP Public Key JWK in IndexedDB
 */
async function storeDPoPPublicKeyJWK(
  jwk: MinimalECPublicKeyJWK
): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const request = store.put(jwk, DPOP_PUBLIC_KEY_JWK_STORAGE_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
      tx.oncomplete = () => db.close();
    });
  } catch (err) {
    console.error("Failed to store DPoP public key JWK:", err);
  }
}

/**
 * Retrieve DPoP Private Key from IndexedDB
 */
export async function getDPoPKey(): Promise<CryptoKey | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(DPOP_PRIVATE_KEY_STORAGE_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

/**
 * Retrieve DPoP Public Key JWK from IndexedDB
 */
export async function getDPoPPublicKeyJWK(): Promise<MinimalECPublicKeyJWK | null> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(DPOP_PUBLIC_KEY_JWK_STORAGE_KEY);
      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result ?? null);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
}

/**
 * Clear stored DPoP Keys from IndexedDB
 */
export async function clearDPoPKey(): Promise<void> {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      store.delete(DPOP_PRIVATE_KEY_STORAGE_KEY);
      store.delete(DPOP_PUBLIC_KEY_JWK_STORAGE_KEY);
      tx.onerror = () => reject(tx.error);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
    });
  } catch {
    // Silently fail
  }
}

/**
 * Generate a new ECDSA P-256 Key Pair for DPoP.
 * Stores both Private Key (CryptoKey) and Public Key (JWK) in IndexedDB.
 * Returns minimal Public Key JWK and its Thumbprint for backend transmission.
 */
export async function generateDPoPKeyPair(): Promise<{
  publicKey: MinimalECPublicKeyJWK;
  thumbprint: string;
}> {
  // 1. Generate Key Pair
  const keyPair = await crypto.subtle.generateKey(
    {
      name: "ECDSA",
      namedCurve: "P-256",
    },
    false, // Private key is non-extractable
    ["sign", "verify"]
  );

  // 2. Export Public Key as full JWK, then convert to minimal
  const fullJwk = await crypto.subtle.exportKey("jwk", keyPair.publicKey);
  const minimalJwk = toMinimalJWK(fullJwk);

  // 3. Calculate Thumbprint
  const thumbprint = await calculateThumbprint(minimalJwk);

  // 4. Store both keys in IndexedDB
  await storeDPoPPrivateKey(keyPair.privateKey);
  await storeDPoPPublicKeyJWK(minimalJwk);

  return { publicKey: minimalJwk, thumbprint };
}

/**
 * Generate a DPoP proof JWT using ECDSA P-256 (ES256)
 * Per RFC 9449 Section 4.2
 *
 * @param httpMethod - HTTP method (GET, POST, etc.)
 * @param httpUri - Full URL of the request (without query/fragment for htu)
 * @param accessToken - Optional access token to bind (adds ath claim)
 */
export async function generateDPoPProof(
  httpMethod: string,
  httpUri: string,
  accessToken?: string
): Promise<string | null> {
  try {
    const privateKey = await getDPoPKey();
    const publicKeyJwk = await getDPoPPublicKeyJWK();

    if (!privateKey || !publicKeyJwk) return null;

    // DPoP header per RFC 9449 Section 4.2
    // MUST include typ, alg, and jwk (public key)
    const header = {
      typ: "dpop+jwt",
      alg: "ES256",
      jwk: publicKeyJwk, // Public key in JWK format
    };

    // Parse URL to get htu without query/fragment
    // Use window.location.origin as base for relative URLs
    const url = new URL(
      httpUri,
      typeof window !== "undefined" ? window.location.origin : undefined
    );
    const htu = `${url.origin}${url.pathname}`;

    // DPoP payload per RFC 9449 Section 4.2
    const payload: Record<string, string | number> = {
      jti: crypto.randomUUID(),
      htm: httpMethod.toUpperCase(),
      htu: htu,
      iat: Math.floor(Date.now() / 1000),
    };

    if (accessToken) {
      const tokenBytes = new TextEncoder().encode(accessToken);
      const hashBuffer = await crypto.subtle.digest("SHA-256", tokenBytes);
      payload.ath = arrayBufferToBase64url(hashBuffer);
    }

    const encodedHeader = arrayBufferToBase64url(
      new TextEncoder().encode(JSON.stringify(header)).buffer as ArrayBuffer
    );
    const encodedPayload = arrayBufferToBase64url(
      new TextEncoder().encode(JSON.stringify(payload)).buffer as ArrayBuffer
    );

    const signatureInput = `${encodedHeader}.${encodedPayload}`;
    const signatureBuffer = await crypto.subtle.sign(
      {
        name: "ECDSA",
        hash: { name: "SHA-256" },
      },
      privateKey,
      new TextEncoder().encode(signatureInput)
    );
    const signature = arrayBufferToBase64url(signatureBuffer);

    return `${encodedHeader}.${encodedPayload}.${signature}`;
  } catch (err) {
    console.error("Failed to generate DPoP proof:", err);
    return null;
  }
}

/**
 * Check if DPoP is enabled (Keys are stored)
 */
export async function isDPoPEnabled(): Promise<boolean> {
  const key = await getDPoPKey();
  return key !== null;
}

  // Legacy export for backward compatibility
export const storeDPoPKey = storeDPoPPrivateKey;

const WebAuthn = {
  isWebAuthnSupported,
  isConditionalUISupported,
  arrayBufferToBase64,
  base64ToArrayBuffer,
  arrayBufferToBase64url,
  base64urlToArrayBuffer,
  stringToArrayBuffer,
  uint8ArrayToString,
  getPasskeyEmail,
  setPasskeyEmail,
  clearPasskeyEmail,
  getPasskeyMobileNumber,
  setPasskeyMobileNumber,
  clearPasskeyMobileNumber,
  toMinimalJWK,
  calculateThumbprint,
  getDPoPKey,
  getDPoPPublicKeyJWK,
  clearDPoPKey,
  generateDPoPKeyPair,
  generateDPoPProof,
  isDPoPEnabled,
  storeDPoPKey,
};

if (typeof window !== "undefined") {
  (window as any).WebAuthn = WebAuthn;
}

const globalObj = (
  typeof window !== "undefined" ? window : globalThis
) as Record<string, unknown>;
globalObj.WebAuthn = WebAuthn;